import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  TextField,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  MenuItem,
  Checkbox,
  FormControl,
  Select,
} from "@mui/material";

/**
 * ProfessionalInfoTab.jsx
 *
 * Props:
 * - formData: object (callType, salesRepName, territoryName, callDateTime, callDuration, accountName, institutionId, attendees (ids), address)
 * - onChange: (field, value) => void
 * - hcps: array of HCP objects (DoctorID, DoctorName, DoctorAddress, ...)
 * - hcos: array of HCO objects (InstitutionID, InstitutionName, InstitutionAddress, ...)
 * - onDoctorSelect: optional fn(doctorObj) -> void
 *
 * Notes:
 * - Account Name (normal call): Autocomplete (searchable)
 * - Institution Name (group call): Autocomplete (searchable)
 * - Attendees (group call): TextField select multiple with MenuItem + Checkbox + ListItemText (same pattern as DetailingPriorityTab)
 * - Address: always visible, disabled, populated from selected doctor or selected institution
 */

function ProfessionalInfoTab({
  formData,
  onChange,
  hcps = [],
  hcos = [],
  onDoctorSelect,
  payload,
}) {
  const [attendeeInput, setAttendeeInput] = useState("");
  const [debouncedAttendeeInput, setDebouncedAttendeeInput] = useState("");
  const [visibleCount, setVisibleCount] = useState(50);
  const loadingMoreRef = useRef(false);

  const subjectOptions = useMemo(() => {
    return Array.isArray(payload?.Call_Subjects) ? payload.Call_Subjects : [];
  }, [payload]);

  const attendeesValues = useMemo(() => {
    return Array.isArray(formData.attendees) ? formData.attendees : [];
  }, [formData.attendees]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedAttendeeInput(attendeeInput), 250);
    return () => clearTimeout(t);
  }, [attendeeInput]);

  useEffect(() => {
    // reset when search changes
    setVisibleCount(50);
  }, [debouncedAttendeeInput]);

  // normalize as string
  const institutionId = useMemo(() => {
    const v = formData.institutionId;
    if (typeof v === "string" && v.trim()) return v.trim();
    return "";
  }, [formData.institutionId]);

  const isGroupCall = formData.callType === "group";
  const isInstitutionSelected = Boolean(institutionId);

  const hcpsByInstitution = useMemo(() => {
    if (!isGroupCall) return hcps || [];
    if (!institutionId) return [];

    return (hcps || []).filter((h) => {
      const docInstId = h?.P_Instutition_ID || h?.P_Institution_ID || "";
      // treat as string; if backend accidentally sends array, still handle it
      if (Array.isArray(docInstId)) {
        return docInstId.some((id) => id === institutionId);
      }
      return String(docInstId) === institutionId;
    });
  }, [hcps, isGroupCall, institutionId]);

  const filteredHcps = useMemo(() => {
    const base = hcpsByInstitution;
    const q = debouncedAttendeeInput.trim().toLowerCase();
    if (!q) return base;

    return base.filter((h) => {
      const name = (h?.DoctorVeevaName || h?.DoctorName || "").toLowerCase();
      const id = (h?.DoctorID || h?.id || "").toLowerCase();
      return name.includes(q) || id.includes(q);
    });
  }, [hcpsByInstitution, debouncedAttendeeInput]);

  const visibleHcps = useMemo(() => {
    return filteredHcps.slice(0, visibleCount);
  }, [filteredHcps, visibleCount]);

  const selectedHcpObjects = useMemo(() => {
    const set = new Set(attendeesValues);
    return (hcps || []).filter((h) => set.has(h?.DoctorID || h?.id));
  }, [hcps, attendeesValues]);

  // ---- Date/Time constraints ----
  const nowLocalValue = useMemo(() => {
    // yyyy-MM-ddTHH:mm (local)
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
      d.getHours(),
    )}:${pad(d.getMinutes())}`;
  }, []);

  const minLocalValue = useMemo(() => {
    // now - 3 days (yyyy-MM-ddTHH:mm local)
    const d = new Date();
    d.setDate(d.getDate() - 3);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
      d.getHours(),
    )}:${pad(d.getMinutes())}`;
  }, []);

  const clampDateTimeLocal = (value) => {
    if (!value) return "";
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return "";

    const max = new Date(); // current moment
    const min = new Date();
    min.setDate(min.getDate() - 3);

    if (dt > max) return nowLocalValue;
    if (dt < min) return minLocalValue;
    return value;
  };

  // ---- Duration constraints ----
  const clampDuration = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    const n = Number(value);
    if (Number.isNaN(n)) return "";
    return Math.max(1, Math.min(180, n));
  };

  const loadMore = () => {
    if (loadingMoreRef.current) return;
    loadingMoreRef.current = true;

    setVisibleCount((prev) => {
      const next = Math.min(prev + 50, filteredHcps.length);
      return next;
    });

    // allow future triggers after state updates
    requestAnimationFrame(() => {
      loadingMoreRef.current = false;
    });
  };

  const selectedDoctor = useMemo(
    () => hcps.find((d) => d?.DoctorID === formData.accountName) || null,
    [hcps, formData.accountName],
  );

  const selectedInstitution = useMemo(() => {
    if (!institutionId) return null;
    return hcos.find((h) => h?.InstitutionID === institutionId) || null;
  }, [hcos, institutionId]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* Sales Rep + Territory */}
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <Box>
            <Box
              sx={{ mb: 1, fontSize: "12px", fontWeight: 600, color: "#333" }}
            >
              Sales Rep Name
            </Box>
            <TextField
              fullWidth
              value={formData.salesRepName || ""}
              variant="outlined"
              disabled
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#e0e0e0" },
                },
              }}
            />
          </Box>

          <Box>
            <Box
              sx={{ mb: 1, fontSize: "12px", fontWeight: 600, color: "#333" }}
            >
              Territory Name
            </Box>
            <TextField
              fullWidth
              value={formData.territoryName || ""}
              variant="outlined"
              disabled
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#e0e0e0" },
                },
              }}
            />
          </Box>
        </Box>

        {/* Date/Time + Duration */}
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <Box>
            <Box
              sx={{ mb: 1, fontSize: "12px", fontWeight: 600, color: "#333" }}
            >
              Date/Time of Call Happened
            </Box>
            <TextField
              fullWidth
              type="datetime-local"
              value={formData.callDateTime || ""}
              inputProps={{
                max: nowLocalValue, // ✅ cannot pick future
                min: minLocalValue, // ✅ cannot pick earlier than last 3 days
              }}
              onChange={(e) => {
                const next = clampDateTimeLocal(e.target.value);
                onChange("callDateTime", next);
              }}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#e0e0e0" },
                },
              }}
            />
          </Box>

          {/* commenting for future use */}
          {/* <Box>
            <Box
              sx={{ mb: 1, fontSize: "12px", fontWeight: 600, color: "#333" }}
            >
              Duration of Call (mins)
            </Box>
            <TextField
              fullWidth
              type="number"
              inputProps={{ min: 1, max: 180 }}
              value={formData.callDuration || ""}
              onChange={(e) =>
                onChange("callDuration", clampDuration(e.target.value))
              }
              onBlur={(e) =>
                onChange("callDuration", clampDuration(e.target.value))
              }
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#e0e0e0" },
                },
              }}
            />
          </Box> */}
        </Box>

        {/* Call Type */}
        <Box>
          <Box sx={{ mb: 1, fontSize: "12px", fontWeight: 600, color: "#333" }}>
            Call Type
          </Box>

          <ToggleButtonGroup
            value={formData.callType || "normal"}
            exclusive
            onChange={(_, val) => {
              if (!val) return;
              if (val === "group") {
                onChange("callType", "group");
                onChange("accountName", "");
                onChange("address", "");
                onChange("institutionId", "");
                onChange("attendees", []);
              } else {
                onChange("callType", "normal");
                onChange("institutionId", "");
                onChange("attendees", []);
                onChange("address", "");
              }
            }}
            sx={{
              "& .MuiToggleButton-root": {
                textTransform: "none",
                borderColor: "#e0e0e0",
              },
            }}
          >
            <ToggleButton value="normal">Direct Call</ToggleButton>
            <ToggleButton value="group">Group Call</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Subject dropdown (new) */}
        <Box>
          <Box sx={{ mb: 1, fontSize: "12px", fontWeight: 600, color: "#333" }}>
            Subject
          </Box>

          <TextField
            fullWidth
            select
            size="small"
            value={formData.subject || ""}
            onChange={(e) => onChange("subject", e.target.value)}
            SelectProps={{
              displayEmpty: true,
              renderValue: (selected) => {
                if (!selected) return "Select subject";
                return selected;
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#fff",
                height: "40px",
                "& fieldset": { borderColor: "#e0e0e0" },
              },
              "& .MuiSelect-select": {
                color: formData.subject ? "#000" : "#9e9e9e",
              },
            }}
          >
            <MenuItem value="" disabled>
              Select subject
            </MenuItem>

            {subjectOptions.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {/* Normal Call: Doctor (searchable) */}
        {!isGroupCall && (
          <Box>
            <Box
              sx={{ mb: 1, fontSize: "12px", fontWeight: 600, color: "#333" }}
            >
              Account Name
            </Box>

            <Autocomplete
              options={hcps}
              value={selectedDoctor}
              onChange={(_, doctor) => {
                if (onDoctorSelect) onDoctorSelect(doctor);
                else {
                  onChange("accountName", doctor?.DoctorID || "");
                  onChange(
                    "address",
                    doctor?.DoctorAddress || doctor?.Doctor_Address || "",
                  );
                }
              }}
              getOptionLabel={(opt) => opt?.DoctorVeevaName || ""} // only
              isOptionEqualToValue={(opt, val) =>
                opt?.DoctorID === val?.DoctorID
              }
              filterOptions={(options, { inputValue }) => {
                const q = (inputValue || "").trim().toLowerCase();
                if (!q) return options;
                return options.filter((opt) =>
                  (opt?.DoctorVeevaName || "").toLowerCase().includes(q),
                );
              }}
              renderOption={(props, option) => {
                // IMPORTANT: remove the default key from props and use DoctorID
                const { key, ...rest } = props;
                return (
                  <li
                    {...rest}
                    key={option?.DoctorID || option?.DoctorVeevaName}
                  >
                    {option?.DoctorVeevaName || ""}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField {...params} placeholder="Search account name" />
              )}
            />
          </Box>
        )}

        {/* Group Call: Institution (searchable) + Attendees (checkbox multi-select) */}
        {isGroupCall && (
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Box>
              <Box
                sx={{ mb: 1, fontSize: "12px", fontWeight: 600, color: "#333" }}
              >
                Institution Name
              </Box>

              <Autocomplete
                options={hcos}
                value={selectedInstitution}
                onChange={(_, inst) => {
                  const instId = inst?.InstitutionID || "";
                  onChange("institutionId", instId);
                  onChange("attendees", []);
                  onChange(
                    "address",
                    inst?.InstitutionAddress ||
                      inst?.InstitutionAddress1 ||
                      inst?.Address ||
                      "",
                  );
                }}
                getOptionLabel={(opt) => opt?.InstitutionName || ""}
                isOptionEqualToValue={(opt, val) =>
                  opt?.InstitutionID === val?.InstitutionID
                }
                filterOptions={(options, { inputValue }) => {
                  const q = (inputValue || "").trim().toLowerCase();
                  if (!q) return options;
                  return options.filter((opt) =>
                    (opt?.InstitutionName || "").toLowerCase().includes(q),
                  );
                }}
                renderOption={(props, option) => {
                  const { key, ...rest } = props;
                  return (
                    <li
                      {...rest}
                      key={option?.InstitutionID || option?.InstitutionName}
                    >
                      {option?.InstitutionName || ""}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search institution name"
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#e0e0e0" },
                      },
                    }}
                  />
                )}
              />
            </Box>

            <Box>
              <Box
                sx={{ mb: 1, fontSize: "12px", fontWeight: 600, color: "#333" }}
              >
                Attendees
              </Box>

              <Autocomplete
                disabled={!isInstitutionSelected}
                multiple
                disableCloseOnSelect
                options={visibleHcps} // ✅ only the visible chunk
                value={selectedHcpObjects} // ✅ selected objects
                inputValue={attendeeInput}
                onInputChange={(_, val, reason) => {
                  // reason can be "input", "reset", "clear"
                  setAttendeeInput(val || "");
                }}
                onChange={(_, selected) => {
                  const ids = (selected || [])
                    .map((x) => x?.DoctorID || x?.id)
                    .filter(Boolean);
                  onChange("attendees", ids);
                }}
                getOptionLabel={(opt) =>
                  opt?.DoctorVeevaName || opt?.DoctorName || opt?.name || ""
                }
                isOptionEqualToValue={(opt, val) =>
                  (opt?.DoctorID || opt?.id) === (val?.DoctorID || val?.id)
                }
                // ✅ IMPORTANT: disable built-in filtering; we filter ourselves (debounced)
                filterOptions={(x) => x}
                ListboxProps={{
                  onScroll: (e) => {
                    const el = e.currentTarget;
                    const nearBottom =
                      el.scrollTop + el.clientHeight >= el.scrollHeight - 24;

                    // load more when near bottom AND more items exist
                    if (nearBottom && visibleCount < filteredHcps.length) {
                      loadMore();
                    }
                  },
                  style: { maxHeight: 320, overflow: "auto" },
                }}
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={option?.DoctorID || option?.id}>
                    <Checkbox checked={selected} sx={{ mr: 1 }} />
                    {option?.DoctorVeevaName ||
                      option?.DoctorName ||
                      option?.name}
                  </li>
                )}
                renderTags={() => null} // ✅ we will render chips below like before
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search attendees"
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#fff",
                        height: "40px",
                        "& fieldset": { borderColor: "#e0e0e0" },
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      // ✅ show "X selected" like before
                      endAdornment: (
                        <>
                          <Box sx={{ mr: 1, fontSize: 12, color: "#666" }}>
                            {attendeesValues.length
                              ? `${attendeesValues.length} selected`
                              : ""}
                          </Box>
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              {/* show selected attendees as chips */}
              {attendeesValues.length > 0 && (
                <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
                  {attendeesValues.map((val) => {
                    const found = hcps.find(
                      (a) => (a?.DoctorID || a?.id) === val,
                    );
                    const label =
                      found?.DoctorVeevaName ||
                      found?.DoctorName ||
                      found?.name ||
                      String(val);

                    return (
                      <Chip
                        key={val}
                        label={label}
                        onDelete={() =>
                          onChange(
                            "attendees",
                            attendeesValues.filter((x) => x !== val),
                          )
                        }
                        sx={{
                          backgroundColor: "#5f8aa5",
                          color: "white",
                          borderRadius: "100px",
                          "& .MuiChip-deleteIcon": { color: "white" },
                        }}
                      />
                    );
                  })}
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Address always visible (disabled) */}
        <Box>
          <Box sx={{ mb: 1, fontSize: "12px", fontWeight: 600, color: "#333" }}>
            Address
          </Box>
          <TextField
            fullWidth
            multiline
            minRows={2}
            value={formData.address || ""}
            disabled
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#e0e0e0" },
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default ProfessionalInfoTab;
